/**
 * DAAily Operations — "Margin of Products" economics engine.
 *
 * Oliver's self-merge zone (`shared/src/operations*.ts`). This file is a PURE,
 * dependency-free port of the one formula chain buried in the 93-column
 * `PRODUCT_MODEL` tab of `ArchDaily_Product_Economics_Model_v04_*.xlsx`.
 * Design record + Phase-B (backend) spec: `docs/OPERATIONS-MARGIN.md`.
 *
 * The chain (all figures in CHF; the caller formats per display currency):
 *
 *   revenueChf   = list price (CHF) x FX->CHF        (FX is INHERITED from the
 *                                                     shared commissions/USL FX
 *                                                     source, never owned here)
 *   salesCostChf = revenueChf x commission %         (by "sales model" bucket)
 *   dtcChf       = revenueChf x DTC %                (by family/category)
 *   cm1Chf       = revenueChf - salesCostChf - dtcChf
 *   resourceCost = per-product override, else the family "delivery cost per
 *                  sale" (a single CHF number per family — the simplification
 *                  that replaces the old 8x7 hours x rate matrix)
 *   cm2Chf       = cm1Chf - resourceCostChf
 *   cm2Pct       = revenueChf === 0 ? 0 : cm2Chf / revenueChf
 *
 * Review flags mirror the spreadsheet's column AQ: CHF price <= 0, commission
 * unmapped, DTC unmapped, family Other/Unknown, resource cost missing, CM2 < 0.
 *
 * `computeModel()` is validated against the Excel's own CM1/CM2 for every one of
 * the ~110 snapshot products — see
 * `web/src/operations/margin/validation/computeModel.excelV04.test.mjs`.
 */
/* ─────────────────────────────── vocab ─────────────────────────────────── */
/** Currencies carried on the price book (the 7 columns of `SOURCE_AdDB`). */
export const MARGIN_CURRENCIES = ['CHF', 'EUR', 'USD', 'GBP', 'MXN', 'CLP', 'CNY'];
/**
 * The commission ("sales model") buckets — each carries one Base/Downside/Upside
 * percentage. Values in `INPUTS_GLOBAL_FAMILY` section 3.
 */
export const MARGIN_COMMISSION_KEYS = [
    'Agent Campaign',
    'Agent Membership New',
    'Agent Membership Renewal',
    'Employee Standard',
    'Other / Unmapped',
];
/** Product families — one "delivery cost per sale" each (section 6, collapsed). */
export const MARGIN_FAMILIES = [
    'Display / Newsletter',
    'Social Media',
    'Listing / Directory',
    'Membership',
    'Content Campaign',
    'Coverage / Sponsorship',
    'Service / Fee',
    'Other / Unknown',
];
/** Direct-transaction-cost categories — one Base/Downside/Upside % each (section 4). */
export const MARGIN_DTC_CATEGORIES = [
    'Standard',
    'Membership',
    'Campaign',
    'Listing',
    'Special / Event',
    'Other / Unmapped',
];
/**
 * Bimodal families keep exactly two profiles (plan decision, not more):
 * Membership `new` vs `renewal`; Coverage/Sponsorship `standard` vs `event`.
 */
export const MARGIN_FAMILY_VARIANTS = ['new', 'renewal', 'standard', 'event'];
export const MARGIN_BANDS = ['base', 'downside', 'upside'];
export function pickBand(b, band) {
    return band === 'downside' ? b.downside : band === 'upside' ? b.upside : b.base;
}
/* ────────────────────────────── engine ───────────────────────────────── */
const isCommissionKey = (s) => MARGIN_COMMISSION_KEYS.includes(s);
const isDtcCategory = (s) => MARGIN_DTC_CATEGORIES.includes(s);
const isFamily = (s) => MARGIN_FAMILIES.includes(s);
/** Choose which list price feeds revenue: CHF if present & positive, else the
 *  first priced currency we have an FX rate for. Mirrors the spreadsheet's
 *  "List Price CHF x FX(CHF)=1" for the common case. */
function resolveRevenue(product, fxToChf) {
    const chf = product.listPrice.CHF;
    if (typeof chf === 'number' && chf > 0) {
        return { currency: 'CHF', listPrice: chf, fx: 1, revenueChf: chf };
    }
    for (const ccy of MARGIN_CURRENCIES) {
        const price = product.listPrice[ccy];
        const fx = ccy === 'CHF' ? 1 : fxToChf[ccy];
        if (typeof price === 'number' && price > 0 && typeof fx === 'number' && fx > 0) {
            return { currency: ccy, listPrice: price, fx, revenueChf: price * fx };
        }
    }
    // Nothing usable — surface the CHF price (may be 0/undefined) for display.
    return { currency: typeof chf === 'number' ? 'CHF' : null, listPrice: chf ?? 0, fx: 1, revenueChf: 0 };
}
function resolveResourceCost(product, a, band, override) {
    if (override?.excludeResourceCost) {
        return { chf: 0, source: 'override' };
    }
    if (override && typeof override.resourceCostChf === 'number') {
        return { chf: override.resourceCostChf, source: 'override' };
    }
    const v = product.familyVariant ?? null;
    if (product.family === 'Membership' && (v === 'new' || v === 'renewal')) {
        return { chf: pickBand(a.membershipVariantDeliveryCostChf[v], band), source: 'family-variant' };
    }
    if (product.family === 'Coverage / Sponsorship' && (v === 'standard' || v === 'event')) {
        return { chf: pickBand(a.coverageVariantDeliveryCostChf[v], band), source: 'family-variant' };
    }
    if (isFamily(product.family)) {
        return { chf: pickBand(a.familyDeliveryCostChf[product.family], band), source: 'family' };
    }
    return { chf: 0, source: 'none' };
}
/**
 * The pure model. Same inputs as the spreadsheet row → same CM1/CM2/CM2%.
 * Never throws; unmapped inputs degrade to 0 + a review flag.
 */
export function computeModel(product, assumptions, opts = {}) {
    const band = opts.band ?? 'base';
    const override = opts.override ?? null;
    const flags = [];
    // A category override reclassifies the product for every lookup below
    // (resource cost, commission %, DTC %) without touching the product
    // record itself — see the field docs on `MarginOverride`.
    const effectiveProduct = override?.family || override?.commissionKey || override?.dtcCategory
        ? {
            ...product,
            family: override.family || product.family,
            commissionKey: override.commissionKey || product.commissionKey,
            dtcCategory: override.dtcCategory || product.dtcCategory,
        }
        : product;
    const rev = resolveRevenue(effectiveProduct, assumptions.fxToChf);
    const inactive = !product.active;
    // Batch option 'perUnit': the list price is per unit and one sale is N units, so revenue per sale is price x N.
    const batchUnits = override?.batchMode === 'perUnit' && (override.batchSize ?? 0) > 1 ? override.batchSize : 1;
    const revenueChf = inactive ? 0 : rev.revenueChf * batchUnits;
    if (!inactive && rev.revenueChf <= 0)
        flags.push('chf-price-non-positive');
    // commission % — the 'Other / Unmapped' bucket exists in the table (at 0) and
    // still raises the review flag; a string outside the vocab is 0 + flag.
    // An explicit `excludeCommission` wins over everything (deliberately not counted).
    let commissionPct;
    if (override?.excludeCommission) {
        commissionPct = 0;
    }
    else if (typeof override?.commissionPct === 'number') {
        commissionPct = override.commissionPct;
    }
    else if (isCommissionKey(effectiveProduct.commissionKey)) {
        commissionPct = pickBand(assumptions.commissionPct[effectiveProduct.commissionKey], band);
        if (effectiveProduct.commissionKey === 'Other / Unmapped')
            flags.push('commission-unmapped');
    }
    else {
        commissionPct = 0;
        flags.push('commission-unmapped');
    }
    // DTC % — mirrors the spreadsheet: 'Other / Unmapped' keeps its table rate
    // (0.10 base) but is still flagged Review; a string outside the vocab is 0 + flag.
    // An explicit `excludeDtc` wins over everything.
    let dtcPct;
    if (override?.excludeDtc) {
        dtcPct = 0;
    }
    else if (typeof override?.dtcPct === 'number') {
        dtcPct = override.dtcPct;
    }
    else if (isDtcCategory(effectiveProduct.dtcCategory)) {
        dtcPct = pickBand(assumptions.dtcPct[effectiveProduct.dtcCategory], band);
        if (effectiveProduct.dtcCategory === 'Other / Unmapped')
            flags.push('dtc-unmapped');
    }
    else {
        dtcPct = 0;
        flags.push('dtc-unmapped');
    }
    if (!isFamily(effectiveProduct.family) || effectiveProduct.family === 'Other / Unknown') {
        flags.push('family-unmapped');
    }
    const salesCostChf = revenueChf * commissionPct;
    const dtcChf = revenueChf * dtcPct;
    const cm1Chf = revenueChf - salesCostChf - dtcChf;
    const res = resolveResourceCost(effectiveProduct, assumptions, band, override);
    const resourceCostChf = inactive ? 0 : res.chf * (override?.batchResourcePerUnit ? batchUnits : 1);
    if (!inactive && res.source === 'none')
        flags.push('resource-cost-missing');
    const cm2Chf = cm1Chf - resourceCostChf;
    const cm2Pct = revenueChf === 0 ? 0 : cm2Chf / revenueChf;
    if (!inactive && cm2Chf < 0)
        flags.push('cm2-negative');
    return {
        band,
        inactive,
        revenueCurrency: rev.currency,
        listPrice: rev.listPrice,
        fxToChf: rev.fx,
        revenueChf,
        batchUnits,
        commissionPct,
        salesCostChf,
        dtcPct,
        dtcChf,
        cm1Chf,
        resourceCostChf,
        resourceCostSource: res.source,
        cm2Chf,
        cm2Pct,
        flags,
    };
}
/** Convert a CHF figure to a display currency using the inherited FX snapshot.
 *  `1 <ccy> = fxToChf[ccy] CHF`, so CHF -> ccy divides. Returns the CHF value
 *  unchanged when the rate is missing (and for CHF itself). */
export function chfToDisplay(valueChf, ccy, fxToChf) {
    if (ccy === 'CHF')
        return valueChf;
    const fx = fxToChf[ccy];
    return typeof fx === 'number' && fx > 0 ? valueChf / fx : valueChf;
}
export const MARGIN_FLAG_LABELS = {
    'chf-price-non-positive': 'No CHF price',
    'commission-unmapped': 'Commission unmapped',
    'dtc-unmapped': 'DTC unmapped',
    'family-unmapped': 'Family unmapped',
    'resource-cost-missing': 'Resource cost missing',
    'cm2-negative': 'CM2 negative',
};
/* ─────────────────────── backend storage (Phase B) ────────────────────── */
/**
 * Firestore collections for the margin backend. All THREE are callable-only
 * (`allow read, write: if false` in `firestore.rules`); every read/write goes
 * through the `operationsMarginApi` callable, which checks the Operations page
 * grant server-side (Michael's condition 1).
 */
export const OPERATIONS_MARGIN_COLLECTIONS = {
    /** One doc per product, id = Salesforce product id. Written by the weekly pull. */
    products: 'operationsMarginProducts',
    /** Single doc `current` — assumptions + the SFDC pull config + editor list. */
    config: 'operationsMarginConfig',
    /** One doc per product id — the per-product exceptions. */
    overrides: 'operationsMarginOverrides',
    /** One doc per product id — the raw survey resource-hour entry (item 3: the
     *  spreadsheet survey, made editable in the tool). */
    surveyEntries: 'operationsMarginSurveyEntries',
    /** One doc per product id — the Phase-2 audit's per-product review candidate
     *  (model-vs-survey gap, classification, recommended action). */
    reviewCandidates: 'operationsMarginReviewCandidates',
    /** Open questions — the audit's Follow-up List + Data Quality Report, merged
     *  into one editable list (Status: open/resolved). */
    followUps: 'operationsMarginFollowUps',
    /** Generic per-datum approval store — "select for approval" (item 5) applies
     *  to ANY data point (an assumption number, an override field, a review
     *  candidate, …), addressed by a stable string key, not a fixed field list. */
    approvals: 'operationsMarginApprovals',
    /** One doc per saved item, private to its owner: a named set of tried values
     *  ("tryOutSet") or a named new product from the calculator ("testProduct").
     *  Never read by the calculation; nothing here is official. */
    savedSets: 'operationsMarginSavedSets',
};
export const OPERATIONS_MARGIN_CONFIG_DOC_ID = 'current';
export const MARGIN_FOLLOW_UP_STATUSES = ['open', 'resolved'];
/** Build a stable approval key. Keep segments short and `:`-joined — never `/`. */
export function marginApprovalKey(kind, ...parts) {
    return [kind, ...parts].map((p) => p.replace(/[/:]/g, '_')).join(':');
}
//# sourceMappingURL=operationsMargin.js.map